import { useCallback, useEffect, useMemo, useState } from "react";
import { MAX_COMMUNITIES_TO_DISPLAY } from "../constants";
import { applyVisibleReorder } from "../helpers/communitySorter";
import type { CommunitySwitcherApi } from "../types/adapters";
import type { CommunitySwitcher } from "../types/CommunitySwitcher";

export interface UseCommunitySortStateOptions {
  readonly isLoadingCommunities: boolean;
  readonly isReorderingCommunities: boolean;
  readonly onSortCommunities: CommunitySwitcherApi["onSortCommunities"];
}

export const useCommunitySortState = (
  communities: CommunitySwitcher[],
  {
    isLoadingCommunities,
    isReorderingCommunities,
    onSortCommunities,
  }: UseCommunitySortStateOptions,
) => {
  const [sortedCommunities, setSortedCommunities] = useState<
    CommunitySwitcher[]
  >([]);

  useEffect(() => {
    if (isLoadingCommunities || isReorderingCommunities) {
      return;
    }
    setSortedCommunities(communities);
  }, [communities, isLoadingCommunities, isReorderingCommunities]);

  const draggableItems = useMemo(
    () => sortedCommunities.slice(0, MAX_COMMUNITIES_TO_DISPLAY),
    [sortedCommunities],
  );

  const pinnedCurrentCommunity = useMemo(() => {
    const currentIndex = sortedCommunities.findIndex(
      c => c.is_current_community,
    );
    if (currentIndex < 0 || currentIndex < MAX_COMMUNITIES_TO_DISPLAY) {
      return null;
    }
    return sortedCommunities[currentIndex] ?? null;
  }, [sortedCommunities]);

  const onSort = useCallback(
    (nextVisible: CommunitySwitcher[]) => {
      const merged = applyVisibleReorder(
        sortedCommunities,
        draggableItems,
        nextVisible,
      );
      setSortedCommunities(merged);
      onSortCommunities(merged, {
        onError: () => {
          setSortedCommunities(communities);
        },
      });
    },
    [communities, draggableItems, onSortCommunities, sortedCommunities],
  );

  return {
    draggableItems,
    onSort,
    pinnedCurrentCommunity,
    sortedCommunities,
  };
};
